import { User } from '@api/models';

/**
 * Checks if a user has completed their basic profile setup
 * Required fields: firstName, lastName, mobileNumber
 */
export function isBasicProfileComplete(user: User): boolean {
  return !!(user.firstName?.trim() && user.lastName?.trim() && user.mobileNumber?.trim());
}

/**
 * Checks if a professional has submitted an ID verification document.
 *
 * Note: this is different from the moderation state. Whether the admin has
 * actually approved the professional is tracked by `professional.status` on
 * the private professional profile — fetch that via `useCurrentProfessional`
 * when you need to gate on approval.
 */
export function isProfessionalDocumentComplete(user: User): boolean {
  if (!user.isProfessional) {
    return true;
  }
  return !!user.verificationDocumentKey?.trim();
}

/**
 * Checks if a professional has completed profile customization (profile picture).
 */
export function isProfessionalProfileCustomizationComplete(user: User): boolean {
  if (!user.isProfessional) {
    return true;
  }
  return !!user.profilePictureKey?.trim();
}

/**
 * Checks if a professional has written a biography.
 *
 * `user.bio` is a localized record (`{ en, fr, es }`) — we treat the bio as
 * complete as soon as at least one locale holds non-empty content, since the
 * public profile falls back across locales.
 */
export function isProfessionalBioComplete(user: User): boolean {
  if (!user.isProfessional) {
    return true;
  }
  const bio = user.bio as Record<string, string | undefined | null> | null | undefined;
  if (!bio) return false;
  return Object.values(bio).some((value) => typeof value === 'string' && value.trim().length > 0);
}

/**
 * Whether the user has finished the onboarding wizard.
 *
 * The wizard now only covers the basic profile (firstName, lastName,
 * mobileNumber) for both learners and mentors. Mentor-specific needs like ID
 * verification and profile picture are handled outside the wizard via the
 * dedicated account sections and the verification banner nudge — we do NOT
 * keep users stuck in the wizard for those.
 */
export function isProfileComplete(user: User): boolean {
  return isBasicProfileComplete(user);
}

/**
 * Returns the onboarding route the user still needs to visit, or null when
 * the wizard is done. See `isProfileComplete` — mentor-specific follow-ups
 * (ID docs, profile picture) are intentionally excluded from the wizard.
 */
export function getOnboardingRedirectPath(user: User): string | null {
  if (!isBasicProfileComplete(user)) {
    return '/onboarding';
  }
  return null;
}
